import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const AssignmentGrid = ({ assignments, selectedAssignments, onSelectAssignment, onSelectAll, onViewDetails, onEditAssignment, onDeleteAssignment }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'assignmentId', direction: 'asc' });

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-success/10 text-success border-success/20',
      pending: 'bg-warning/10 text-warning border-warning/20',
      expired: 'bg-muted text-muted-foreground border-border',
      upcoming: 'bg-primary/10 text-primary border-primary/20'
    };
    return colors?.[status] || colors?.expired;
  };

  const getStatusIcon = (status) => {
    const icons = {
      active: 'CheckCircle2',
      pending: 'Clock',
      expired: 'XCircle',
      upcoming: 'Calendar'
    };
    return icons?.[status] || 'Circle';
  };

  const getRoleColor = (role) => {
    return role === 'primary' ?'bg-primary/10 text-primary border-primary/20' :'bg-secondary/10 text-secondary border-secondary/20';
  };

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig?.key === key && sortConfig?.direction === 'asc' ? 'desc' : 'asc'
    });
  };

  const sortedAssignments = [...assignments]?.sort((a, b) => {
    if (sortConfig?.key) {
      const aValue = a?.[sortConfig?.key];
      const bValue = b?.[sortConfig?.key];
      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const SortIcon = ({ columnKey }) => {
    if (sortConfig?.key !== columnKey) {
      return <Icon name="ChevronsUpDown" size={14} color="var(--color-muted-foreground)" strokeWidth={2} />;
    }
    return sortConfig?.direction === 'asc' 
      ? <Icon name="ChevronUp" size={14} color="var(--color-primary)" strokeWidth={2} />
      : <Icon name="ChevronDown" size={14} color="var(--color-primary)" strokeWidth={2} />;
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-1 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left">
                <Checkbox
                  checked={selectedAssignments?.length === assignments?.length && assignments?.length > 0}
                  onChange={(e) => onSelectAll(e?.target?.checked)}
                />
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('assignmentId')}
                  className="flex items-center space-x-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-hover"
                >
                  <span>ID</span>
                  <SortIcon columnKey="assignmentId" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('driverName')}
                  className="flex items-center space-x-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-hover"
                >
                  <span>Driver</span>
                  <SortIcon columnKey="driverName" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('unitNumber')}
                  className="flex items-center space-x-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-hover"
                >
                  <span>Unit</span>
                  <SortIcon columnKey="unitNumber" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('yardLocation')}
                  className="flex items-center space-x-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-hover"
                >
                  <span>Yard</span>
                  <SortIcon columnKey="yardLocation" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('roleType')}
                  className="flex items-center space-x-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-hover"
                >
                  <span>Role</span>
                  <SortIcon columnKey="roleType" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('startDate')}
                  className="flex items-center space-x-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-hover"
                >
                  <span>Start Date</span>
                  <SortIcon columnKey="startDate" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('endDate')}
                  className="flex items-center space-x-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-hover"
                >
                  <span>End Date</span>
                  <SortIcon columnKey="endDate" />
                </button>
              </th>
              <th className="px-4 py-3 text-left">
                <button
                  onClick={() => handleSort('status')}
                  className="flex items-center space-x-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider hover:text-foreground transition-hover"
                >
                  <span>Status</span>
                  <SortIcon columnKey="status" />
                </button>
              </th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {sortedAssignments?.map((assignment) => (
              <tr
                key={assignment?.assignmentId}
                className="hover:bg-muted/30 transition-hover cursor-pointer"
                onClick={() => onViewDetails(assignment)}
              >
                <td className="px-4 py-3" onClick={(e) => e?.stopPropagation()}>
                  <Checkbox
                    checked={selectedAssignments?.includes(assignment?.assignmentId)}
                    onChange={(e) => onSelectAssignment(assignment?.assignmentId, e?.target?.checked)}
                  />
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm font-medium text-foreground">{assignment?.assignmentId}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center justify-center w-8 h-8 bg-primary/10 text-primary rounded-full text-xs font-semibold">
                      {assignment?.driverName?.split(' ')?.map(n => n?.[0])?.join('')}
                    </div>
                    <span className="text-sm text-foreground">{assignment?.driverName}</span>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm font-medium text-foreground">{assignment?.unitNumber}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-muted-foreground">{assignment?.yardLocation}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md border ${getRoleColor(assignment?.roleType)}`}>
                    {assignment?.roleType === 'primary' ? 'Primary' : 'Relief'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-muted-foreground">{assignment?.startDate}</span>
                </td>
                <td className="px-4 py-3">
                  <span className="text-sm text-muted-foreground">{assignment?.endDate || 'Ongoing'}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex items-center space-x-1 px-2 py-1 text-xs font-medium rounded-md border ${getStatusColor(assignment?.status)}`}>
                    <Icon name={getStatusIcon(assignment?.status)} size={12} strokeWidth={2} />
                    <span className="capitalize">{assignment?.status}</span>
                  </span>
                </td>
                <td className="px-4 py-3" onClick={(e) => e?.stopPropagation()}>
                  <div className="flex items-center justify-end space-x-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Eye"
                      onClick={() => onViewDetails(assignment)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Edit"
                      onClick={() => onEditAssignment(assignment)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      iconName="Trash2"
                      onClick={() => onDeleteAssignment(assignment)}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sortedAssignments?.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12">
          <Icon name="FileX" size={48} color="var(--color-muted-foreground)" strokeWidth={1.5} />
          <p className="mt-4 text-sm text-muted-foreground">No assignments found</p>
        </div>
      )}
    </div>
  );
};

export default AssignmentGrid;